import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Step, Workflow } from './Workflow';
import useFeature from 'hooks/useFeature';
import { selectTenant, FeatureGroups } from 'state/tenant';
import { selectDriversLicense } from 'state/driversLicense';
import useResponsiveQuery from 'hooks/useResponsiveQuery';
import { reject, not, keys } from 'ramda';
import useQueryParameters from 'hooks/useQueryParameters';

const useIcDriverLicenseWorkflow = () => {
  const tenant = useSelector(selectTenant);
  const { selectedCardKey } = useQueryParameters();
  const { result: driversLicense } = useSelector(selectDriversLicense);
  const { isGroupEnabled } = useFeature();
  const { isEnabled } = useFeature();
  const { isMobile } = useResponsiveQuery();
  const getWorkflow = useCallback((): Workflow => {
    const hasCardChoice = tenant.enabledFeatures.cardChoices.length > 0;
    const hasProductChoice = tenant.enabledFeatures.productChoices.length > 0;
    const hasConsumerSearch = driversLicense !== 'success';

    const stepsEnabled = {
      [Step.CARD_SELECTOR]: isEnabled('cardSelector') && !selectedCardKey,

      [Step.PRODUCT_CHOICE]: hasProductChoice,

      [Step.CARD_CHOICE]: hasCardChoice,

      [Step.DRIVER_LICENSE_START]: true,

      [Step.SCAN_PAGE]: true,

      [Step.CONSUMER_SEARCH]: hasConsumerSearch,

      [Step.APPLICATION]: true,

      [Step.APPLICATION_2]: isMobile,

      [Step.ADD_ONS]: isGroupEnabled(FeatureGroups['add-ons']),

      [Step.REVIEW_TERMS]: true,
    };

    const progressStartStep = Step.APPLICATION;

    const firstStep = ((keys(
      reject(not, stepsEnabled),
    ) as unknown) as Step[])[0];

    const paths = {
      [Step.CARD_SELECTOR]: '/card-selector',
      [Step.PRODUCT_CHOICE]: '/product-choice',
      [Step.CARD_CHOICE]: '/card-choice',
      [Step.DRIVER_LICENSE_START]: '/driver-license/start',
      [Step.SCAN_PAGE]: '/driver-license/scan-page',
      [Step.CONSUMER_SEARCH]: '/consumer-search',
      [Step.APPLICATION]: '/credit-application',
      [Step.APPLICATION_2]: '/credit-application/2',
      [Step.ADD_ONS]: '/add-ons',
      [Step.REVIEW_TERMS]: '/review-terms',
      ...{
        [firstStep]: '/driver-license',
      },
    };

    return {
      stepsEnabled,
      paths,
      progressStartStep,
      restartStep: firstStep,
      timeoutRestartPath: '/driver-license',
    };
  }, [
    tenant.enabledFeatures.cardChoices.length,
    tenant.enabledFeatures.productChoices.length,
    driversLicense,
    isEnabled,
    selectedCardKey,
    isMobile,
    isGroupEnabled,
  ]);

  return getWorkflow;
};

export default useIcDriverLicenseWorkflow;
